import { Button, Intent, Menu, MenuItem, Position } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { Popover2 } from '@blueprintjs/popover2';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { Body, StaticBadge } from 'vet-bones/design-system/components';
import { useIsMounted } from 'vet-bones/utils/hooks';

import { useAppDispatch } from 'src/ui/app/hooks';
import { withConfirmationDs } from 'src/ui/components/withConfirmationDs';
import {
  MANAGE_API_TOKEN_DELETE,
  MANAGE_API_TOKEN_DELETE_CANCEL,
  MANAGE_API_TOKEN_DELETE_SUBMIT,
  MANAGE_API_TOKEN_EXPIRED,
  MANAGE_API_TOKEN_EXPIRES,
  MANAGE_API_TOKEN_MENU,
  MANAGE_API_TOKEN_ROW,
} from 'src/ui/constants/testIds';
import { API_TOKEN_DELETED } from 'src/ui/constants/trackingEventNames';
import { queueNotification } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  ApiToken,
  Scalars,
  useDeleteApiTokenMutation,
  useTrackEventMutation,
} from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import * as copy from 'src/ui/templates/copy';

interface ApiTokenRowProps {
  apiToken: ApiToken;
  appId?: string;
  idx?: number;
}

// The expiry is a calendar date with no timezone. The Date constructor would
// read it as UTC midnight, which is the previous day anywhere west of
// Greenwich, so it is built from its parts as a local date instead.
const parseDate = (value: Scalars['Datetime']) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDate = (maybeDate: Scalars['Datetime'] | undefined) =>
  maybeDate ? parseDate(maybeDate).toLocaleDateString() : '';
const formatDateTime = (maybeDate: Scalars['Datetime'] | undefined) =>
  maybeDate ? new Date(maybeDate).toLocaleString() : '';

export const ApiTokenRow: React.VFC<ApiTokenRowProps> = ({
  apiToken,
  appId,
  idx,
}) => {
  const isMounted = useIsMounted();
  const dispatch = useAppDispatch();
  const onError = useOnError();
  const onMutate = useOnMutate();
  const queryClient = useQueryClient();
  const { mutate: trackEvent } = useTrackEventMutation(graphQLClient, {});

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);

  const kind = appId ? 'voicebox' : 'user';
  const deleteCopy = copy.components.manageApiTokens.apiTokenRow.delete;
  // The server treats a token as expired from the start of its expiry date
  // in UTC, so the badge follows the UTC calendar rather than the browser's.
  // Both sides are YYYY-MM-DD strings, which compare correctly as text.
  const todayUtc = new Date().toISOString().slice(0, 10);
  const isExpired = apiToken.expires <= todayUtc;

  const { mutate: deleteApiToken } = useDeleteApiTokenMutation(graphQLClient, {
    onError,
    onMutate,
    onSuccess: async (data) => {
      if (data.deleteApiToken?.success) {
        trackEvent({
          input: {
            event: API_TOKEN_DELETED,
            properties: JSON.stringify({ kind, expired: isExpired }),
          },
        });
      } else {
        dispatch(
          queueNotification(data.deleteApiToken?.error || deleteCopy.failed)
        );
      }
      // The list is refreshed either way: a refused delete can mean the token
      // was already gone. cancelRefetch makes this a fresh fetch rather than
      // joining one already in flight that may predate the delete.
      await queryClient.refetchQueries(['listApiTokens'], undefined, {
        cancelRefetch: true,
      });
      // The row is gone once the list no longer has the token.
      if (!isMounted.current) {
        return;
      }
      setIsMenuOpen(false);
    },
  });

  const popoverMenu = (
    <Menu>
      <MenuItem
        data-testid={MANAGE_API_TOKEN_DELETE}
        icon={IconNames.TRASH}
        text={deleteCopy.submit}
        intent={Intent.DANGER}
        onClick={() =>
          withConfirmationDs({
            message: deleteCopy[kind].message(apiToken.name || ''),
            noPrompt: deleteCopy.cancel,
            noPromptTestId: MANAGE_API_TOKEN_DELETE_CANCEL,
            onConfirmationSuccess: () => {
              deleteApiToken({ input: { id: apiToken.id, app_id: appId } });
            },
            title: deleteCopy[kind].title,
            yesPrompt: deleteCopy.submit,
            yesPromptTestId: MANAGE_API_TOKEN_DELETE_SUBMIT,
          })
        }
      />
    </Menu>
  );

  const popover = (
    <Popover2
      minimal
      content={popoverMenu}
      position={Position.LEFT_TOP}
      isOpen={isMenuOpen}
      onClose={(e) => {
        if (e) {
          // prevent triggering card link when clicking menu items
          e.stopPropagation();
          setIsMenuOpen(false);
        }
      }}
    >
      <Button
        minimal
        active={isMenuOpen}
        data-testid={MANAGE_API_TOKEN_MENU}
        icon={IconNames.MORE}
        onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
          if (e) {
            e.stopPropagation();
            setIsMenuOpen(!isMenuOpen);
          }
        }}
      />
    </Popover2>
  );

  return (
    <tr data-testid={MANAGE_API_TOKEN_ROW(apiToken.id, idx)}>
      <td>
        <div className="sd-api-token-name">
          <strong>{apiToken.name}</strong>
        </div>
      </td>
      <td>{formatDateTime(apiToken.created)}</td>
      <td>{formatDateTime(apiToken.last_used)}</td>
      <td>
        <div className="sd-api-token-expires">
          <Body size="md" testId={MANAGE_API_TOKEN_EXPIRES}>
            {formatDate(apiToken.expires)}
          </Body>
          {isExpired ? (
            <StaticBadge
              size="xs"
              testId={MANAGE_API_TOKEN_EXPIRED}
              type="warning"
              variant="muted"
            >
              {copy.components.manageApiTokens.apiTokenRow.expired}
            </StaticBadge>
          ) : null}
        </div>
      </td>
      <td>{popover}</td>
    </tr>
  );
};
